import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Chip,
  Tooltip,
  Divider
} from '@mui/material';
import { formatCurrency, formatNumber, formatQuantity } from './formatters';

interface LossesData {
  valor_total_perdas?: number;
  icms_total_perdas?: number;
  total_transacoes_perdas?: number;
  quantidade_total_perdas?: number;
  percentual_perdas?: number;
  preco_medio_perdas?: number;
  ncm_info?: {
    ncm: string;
    descricao_ncm: string;
  };
}

interface DrillDownLossesInfoProps {
  lossesData: LossesData;
  title?: string;
}

const DrillDownLossesInfo: React.FC<DrillDownLossesInfoProps> = ({ 
  lossesData, 
  title = "Análise de Perdas" 
}) => {
  if (!lossesData) return null;

  return (
    <Grid item xs={12}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'error.main', mb: 2 }}>
          📉 {title}
        </Typography>

        {/* Informações do NCM */}
        {lossesData.ncm_info && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Informações Fiscais:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                NCM:
              </Typography>
              <Tooltip title={lossesData.ncm_info.descricao_ncm || 'Descrição não disponível'}>
                <Chip 
                  label={lossesData.ncm_info.ncm}
                  color="info"
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
              </Tooltip>
            </Box>
          </Box>
        )}

        {/* Percentual de Perdas */}
        {typeof lossesData.percentual_perdas === 'number' && (
          <Box sx={{ mb: 2 }}>
            <Chip 
              label={`${lossesData.percentual_perdas.toFixed(1)}% de Perdas`}
              color={lossesData.percentual_perdas > 5 ? 'error' : lossesData.percentual_perdas > 2 ? 'warning' : 'success'}
              size="medium"
              variant="filled"
              sx={{ fontWeight: 600, fontSize: '0.9rem' }}
              title="Percentual de perdas sobre o valor total movimentado"
            />
          </Box>
        )}

        <Grid container spacing={3}>
          {/* Valores das Perdas */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
              💸 Valores das Perdas
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip 
                label={`Valor Perdas: ${formatCurrency(lossesData.valor_total_perdas || 0)}`}
                color="error"
                variant="outlined"
                size="medium"
                sx={{ minWidth: 'auto', fontSize: '0.85rem' }}
                title="Valor total dos produtos perdidos"
              />
              <Chip 
                label={`ICMS Perdas: ${formatCurrency(lossesData.icms_total_perdas || 0)}`}
                color="warning"
                variant="outlined"
                size="medium"
                sx={{ minWidth: 'auto', fontSize: '0.85rem' }}
                title="Valor total do ICMS sobre as perdas"
              />
              <Chip 
                label={`Transações: ${formatNumber(lossesData.total_transacoes_perdas || 0)}`}
                color="info"
                variant="outlined"
                size="medium"
                sx={{ minWidth: 'auto', fontSize: '0.85rem' }}
                title="Número de transações de perdas registradas"
              />
              <Chip 
                label={`Quantidade: ${formatQuantity(lossesData.quantidade_total_perdas || 0)} un.`}
                color="secondary"
                variant="outlined"
                size="medium"
                sx={{ minWidth: 'auto', fontSize: '0.85rem' }}
                title="Quantidade total de produtos perdidos"
              />
            </Box>
          </Grid>

          {/* Análise Adicional */}
          {typeof lossesData.preco_medio_perdas === 'number' && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                📊 Análise Adicional
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip 
                  label={`Preço Médio das Perdas: ${formatCurrency(lossesData.preco_medio_perdas)}`}
                  color="secondary"
                  variant="outlined"
                  size="medium"
                  sx={{ minWidth: 'auto', fontSize: '0.85rem' }}
                  title="Preço médio unitário dos produtos perdidos"
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
      <Divider sx={{ mb: 3 }} />
    </Grid>
  );
};

export default DrillDownLossesInfo; 